import React, { useState, useEffect, useContext } from "react";
import "../../App.css";
import { FaTimes } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXTwitter, faFacebook, faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { UserContext } from "../context/UserContext";
import DonationBox from "./DonateForm";
import { BsShare } from "react-icons/bs";
import { AiOutlineInfoCircle } from 'react-icons/ai';

const Politician = () => {
  const [selectedPoliticianId, setSelectedPoliticianId] = useState(
    localStorage.getItem("selectedPoliticianId") || ""
  );
  const [politicianData, setPoliticianData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSharePopupVisible, setIsSharePopupVisible] = useState(false);
  const [showDonationBox, setShowDonationBox] = useState(false);
  const [showBiographyPopup, setShowBiographyPopup] = useState(false);
  const { emailId } = useContext(UserContext);
  const isAdmin = emailId === "info@urcommunity.com.au";

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const politicianId = urlParams.get("politicianId") || urlParams.get("selectedPoliticianId");
    const selectionType = urlParams.get("selectionType");

    // If URL parameters exist, they take precedence
    if (politicianId && selectionType === "politician") {
      setSelectedPoliticianId(politicianId);
      localStorage.setItem("selectedPoliticianId", politicianId);
      localStorage.setItem("selectionType", "politician");
    } else {
      // Only use localStorage if no URL parameters exist
      const storedId = localStorage.getItem("selectedPoliticianId");
      if (storedId && storedId !== selectedPoliticianId) {
        setSelectedPoliticianId(storedId);
      }
    }

    // Add event listener for storage changes
    const handleStorageChange = (e) => {
      if (e.key === "selectedPoliticianId") {
        const newPoliticianId = e.newValue;
        if (newPoliticianId && newPoliticianId !== selectedPoliticianId) {
          setSelectedPoliticianId(newPoliticianId);
        }
      }
    };

    // Add event listener for immediate updates
    const handlePoliticianSelected = (e) => {
      const newPoliticianId = e.detail.politicianId;
      if (newPoliticianId && newPoliticianId !== selectedPoliticianId) {
        setSelectedPoliticianId(newPoliticianId);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("politicianSelected", handlePoliticianSelected);
    
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("politicianSelected", handlePoliticianSelected);
    };
  }, [selectedPoliticianId]);

  useEffect(() => {
    const fetchPoliticianData = async () => {
      if (!selectedPoliticianId) return;

      try {
        setIsLoading(true);
        console.log("🔍 Fetching politician data for ID:", selectedPoliticianId);
        
        const response = await fetch(
          `/api/politicians/${selectedPoliticianId}`
        );
        
        if (!response.ok) {
          const text = await response.text();
          console.error('Server response:', text);
          throw new Error(`Failed to fetch politician data: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        console.log("✅ Politician data received:", data);
        
        // Fetch donations for politician
        try {
          console.log("�� Fetching donations for politician ID:", selectedPoliticianId);
          const donationsResponse = await fetch(
            `/api/politician-donations/${selectedPoliticianId}`
          );
          
          if (!donationsResponse.ok) {
            console.error('Donations response:', await donationsResponse.text());
            throw new Error('Failed to fetch donations data');
          }

          const donationsData = await donationsResponse.json();
          console.log("✅ Donations data received:", donationsData);
          
          setPoliticianData({
            ...data,
            donations: donationsData.donations || [],
            total_raised: donationsData.totalRaised || 0,
            backer_count: donationsData.backerCount || 0
          });
        } catch (donationsError) {
          console.error("Error fetching donations:", donationsError);
          setPoliticianData({
            ...data,
            donations: [],
            total_raised: 0,
            backer_count: 0
          });
        }
      } catch (err) {
        setError(err.message);
        console.error("Error fetching politician data:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPoliticianData();
  }, [selectedPoliticianId]);

  const handleEdit = () => {
    // TODO: Implement edit functionality
  };

  const handleDelete = async () => {
    if (!isAdmin || !selectedPoliticianId) return;

    if (window.confirm("Are you sure you want to delete this politician?")) {
      try {
        const response = await fetch(
          `/api/politicians/${selectedPoliticianId}`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ created_by: emailId }),
          }
        );

        if (response.ok) {
          alert("Politician deleted successfully!");
          localStorage.setItem("selectedPoliticianId", "");
          setPoliticianData(null);
        } else {
          throw new Error("Failed to delete politician");
        }
      } catch (error) {
        console.error("Error deleting politician:", error);
        alert("Failed to delete politician. Please try again.");
      }
    }
  };

  const generateShareableLink = () => {
    if (!selectedPoliticianId) {
      alert("No politician selected for sharing.");
      return;
    }


    const shareUrl = `${window.location.origin}/main/?selectedPoliticianId=${selectedPoliticianId}&selectionType=politician`;
    navigator.clipboard
      .writeText(shareUrl)
      .then(() => {
        alert("Link copied to clipboard! Share it with your friends.");
      })
      .catch((err) => {
        console.error("Failed to copy:", err);
      });

    return shareUrl;
  };

  const shareToSocialMedia = (platform) => {
    const shareUrl = generateShareableLink();
    if (!shareUrl) return;

    const message = `Check out this politician on UrVote!`;
    const encodedUrl = encodeURIComponent(shareUrl);
    let socialUrl = "";

    switch (platform) {
      case "twitter":
        socialUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          message
        )}&url=${encodedUrl}`;
        break;
      case "facebook":
        socialUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case "whatsapp":
        socialUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}%20${encodedUrl}`;
        break;
      default:
        alert("Unsupported platform");
        return;
    }

    window.open(socialUrl, "_blank");
  };

  if (isLoading) return <div style={{ color: "white" }}>Loading...</div>;
  if (error) return <div style={{ color: "white" }}>Error: {error}</div>;
  if (!politicianData) return <div style={{ color: "white" }}>No politician found</div>;

  return (
    <div style={{
      padding: window.innerWidth <= 926 ? "10px" : "2px",
      borderRadius: window.innerWidth <= 926 ? "0" : "10px",
    }}>
      <div style={{ 
        display: "flex", 
        flexDirection: window.innerWidth <= 926 ? "column" : "row",
        gap: window.innerWidth <= 926 ? "8px" : "10px"
      }}>
        {/* Title Section - Mobile */}
        {window.innerWidth <= 926 && (
          <div style={{
            marginBottom: "8px",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start"
          }}>
            <div style={{ 
              flex: 1,
              textAlign: "left"
            }}>
              <h2 style={{ 
                fontSize: "20px",
                marginBottom: "3px",
                fontWeight: "normal",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}>
                {politicianData.name}
                <div style={{ position: "relative", display: "inline-block" }}>
                  <FontAwesomeIcon 
                    icon={faCircleInfo}
                    color="white" 
                    size="sm"
                    style={{ 
                      cursor: "pointer",
                      transform: "scale(1.1)"
                    }}
                    onMouseEnter={(e) => {
                      const tooltip = e.currentTarget.nextElementSibling;
                      tooltip.style.visibility = "visible";
                      tooltip.style.opacity = "1";
                    }}
                    onMouseLeave={(e) => {
                      const tooltip = e.currentTarget.nextElementSibling;
                      tooltip.style.visibility = "hidden";
                      tooltip.style.opacity = "0";
                    }}
                  />
                  <div style={{
                    visibility: "hidden",
                    opacity: "0",
                    position: "absolute",
                    top: "100%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: "rgba(0, 0, 0, 0.9)",
                    color: "white",
                    padding: "10px",
                    borderRadius: "5px",
                    width: "250px",
                    fontSize: "12px",
                    zIndex: 1000,
                    transition: "opacity 0.3s",
                    textAlign: "left",
                    marginTop: "5px",
                    boxShadow: "0 2px 5px rgba(0,0,0,0.2)"
                  }}>
                    Candidates shown here are listed based on AEC data. Those who've engaged with UrVote are ranked by values alignment. Others appear as "Unranked" until they participate. UrVote does not endorse any candidate or party. For more information, please view our full Disclaimer policy.
                  </div>
                </div>
              </h2>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "15px",
                fontSize: "14px",
                color: "#cccccc",
                marginBottom: "3px"
              }}>
                <span>Member for {politicianData.constituency}</span>
                <span>Party: {politicianData.party}</span>
              </div>
              <div style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                fontSize: "12px",
                marginTop: "3px",
                gap: "5px",
                width: "100%"
              }}>
                <span style={{ textAlign: "center" }}>Position: {politicianData.position}</span>
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                  marginTop: "3px",
                  padding: "0 10px"
                }}>
                  <span>Chamber: {politicianData.chamber}</span>
                  <span>State: {politicianData.state}</span>
                </div>
              </div>
            </div>
            
            {/* Share Icon Button for Mobile */}
            <button
              onClick={() => setIsSharePopupVisible(true)}
              style={{
                padding: "8px",
                background: "#087cfc",
                color: "white",
                borderRadius: "5px",
                cursor: "pointer",
                height: "fit-content",
                marginLeft: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <BsShare size={16} style={{ 
                strokeWidth: 1,
                fontWeight: "bold",
                transform: "scale(1.2)"
              }} />
            </button>
          </div>
        )}

        {/* Image Section */}
        <div style={{ 
          width: window.innerWidth <= 926 ? "100%" : "35%",
          marginBottom: window.innerWidth <= 926 ? "5px" : "0",
          aspectRatio: "4/3",
          overflow: "hidden"
        }}>
          <img
            src={politicianData.image_url}
            alt={politicianData.name}
            style={{
              width: "100%",
              height: window.innerWidth <= 926 ? "200px" : "240px",
              borderRadius: "5px",
              border: "none",
              objectFit: "contain",
              objectPosition: "center",
              backgroundColor: "transparent",
              padding: "5px"
            }}
          />
          {/* Add Biography button for desktop layout */}
          {window.innerWidth > 926 && (
            <button
              onClick={() => setShowBiographyPopup(true)}
              style={{
                width: "100%",
                padding: "12px",
                marginTop: "5px",
                background: "linear-gradient(to right, #007bff, #00d4ff)",
                color: "white",
                border: "none",
                borderRadius: "5px",
                fontSize: "14px",
                fontWeight: "bold",
                cursor: "pointer"
              }}
            >
              Biography
            </button>
          )}
        </div>

        {/* Action Buttons - Mobile Only */}
        {window.innerWidth <= 926 && (
          <div style={{
            display: "flex",
            gap: "8px",
            marginTop: "5px"
          }}>
            <button
              onClick={() => setShowBiographyPopup(true)}
              style={{
                flex: 1,
                padding: "12px",
                backgroundColor: "#007bff",
                color: "white",
                border: "none",
                borderRadius: "5px",
                fontSize: "14px",
                fontWeight: "bold"
              }}
            >
              Biography
            </button>
            <button
              onClick={() => setShowDonationBox(true)}
              disabled={politicianData && politicianData.is_candidate !== null && politicianData.is_candidate !== ""}
              style={{
                flex: 1,
                padding: "12px",
                backgroundColor: politicianData && politicianData.is_candidate !== null && politicianData.is_candidate !== "" ? "#ccc" : "#007bff",
                color: "white",
                border: "none",
                borderRadius: "5px",
                fontSize: "14px",
                fontWeight: "bold",
                cursor: politicianData && politicianData.is_candidate !== null && politicianData.is_candidate !== "" ? "not-allowed" : "pointer",
                title: politicianData && politicianData.is_candidate !== null && politicianData.is_candidate !== "" ? "Donations are disabled for candidates." : "Give Now"
              }}
            >
              Give Now
            </button>
          </div>
        )}

        {/* Description Section - Mobile Only */}
        {window.innerWidth <= 926 && (
          <div style={{
            marginTop: "15px"
          }}>
            <h3 style={{
              fontSize: "18px",
              color: "white",
              marginBottom: "8px"
            }}>Description</h3>
            <div style={{
              backgroundColor: "white",
              padding: "15px",
              borderRadius: "10px",
              maxHeight: "200px",
              overflowY: "auto"
            }}>
              {politicianData.description
                ? politicianData.description.split("\n").map((paragraph, index) => (
                    <p key={index} style={{
                      fontSize: "14px",
                      marginBottom: "10px"
                    }}>
                      {paragraph.trim()}
                    </p>
                  ))
                : <p style={{ fontSize: "14px", color: "#888" }}>No description available.</p>
              }
            </div>
          </div>
        )}

        {/* Desktop layout remains unchanged */}
        {window.innerWidth > 926 && (
          <div style={{ flex: "2" }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
              <h3 style={{ 
                fontSize: "20px", 
                color: "white",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}>
                {politicianData.name}
                <div style={{ position: "relative", display: "inline-block" }}>
                  <FontAwesomeIcon 
                    icon={faCircleInfo}
                    color="white" 
                    size="sm"
                    style={{ 
                      cursor: "pointer",
                      transform: "scale(1.1)"
                    }}
                    onMouseEnter={(e) => {
                      const tooltip = e.currentTarget.nextElementSibling;
                      tooltip.style.visibility = "visible";
                      tooltip.style.opacity = "1";
                    }}
                    onMouseLeave={(e) => {
                      const tooltip = e.currentTarget.nextElementSibling;
                      tooltip.style.visibility = "hidden";
                      tooltip.style.opacity = "0";
                    }}
                  />
                  <div style={{
                    visibility: "hidden",
                    opacity: "0",
                    position: "absolute",
                    top: "100%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: "rgba(0, 0, 0, 0.9)",
                    color: "white",
                    padding: "10px",
                    borderRadius: "5px",
                    width: "250px",
                    fontSize: "12px",
                    zIndex: 1000,
                    transition: "opacity 0.3s",
                    textAlign: "left",
                    marginTop: "5px",
                    boxShadow: "0 2px 5px rgba(0,0,0,0.2)"
                  }}>
                    Candidates shown here are listed based on AEC data. Those who've engaged with UrVote are ranked by values alignment. Others appear as "Unranked" until they participate. UrVote does not endorse any candidate or party. For more information, please view our full Disclaimer policy.
                  </div>
                </div>
              </h3>
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  onClick={() => setIsSharePopupVisible(true)}
                  style={{
                    padding: "8px 12px",
                    background: "#087cfc",
                    color: "white",
                    border: "none",
                    borderRadius: "5px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Share
                </button>
                <button
                  onClick={() => setShowDonationBox(true)}
                  disabled={politicianData && politicianData.is_candidate !== null && politicianData.is_candidate !== ""}
                  style={{
                    padding: "8px 12px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "white",
                    background: politicianData && politicianData.is_candidate !== null && politicianData.is_candidate !== "" ? "#ccc" : "#007bff",
                    border: "none",
                    borderRadius: "5px",
                    cursor: politicianData && politicianData.is_candidate !== null && politicianData.is_candidate !== "" ? "not-allowed" : "pointer",
                    title: politicianData && politicianData.is_candidate !== null && politicianData.is_candidate !== "" ? "Donations are disabled for candidates." : "Give Now"
                  }}
                >
                  Give Now
                </button>
              </div>
            </div>

            <div style={{ 
              marginTop: "5px",
              display: "flex", 
              alignItems: "center", 
              justifyContent: "space-between",
              width: "100%",
              color: "white", 
              fontSize: "16px",
            }}>
              <span>Member for {politicianData.constituency}</span>
              <span style={{ 
                display: "flex",
                alignItems: "center",
                gap: "5px",
                marginLeft: "auto"
              }}>
                Party: {politicianData.party}
              </span>
            </div>

            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              color: "white",
              fontSize: "14px",
              marginTop: "5px"
            }}>
              <span>Position: {politicianData.position}</span>
              <span style={{ 
                textAlign: "center",
                marginLeft: "40px",
                flex: "1",
                display: "flex",
                justifyContent: "center"
              }}>
                Chamber: {politicianData.chamber}
              </span>
              <span style={{
                marginLeft: "auto"
              }}>
                State: {politicianData.state}
              </span>
            </div>

            <h4 style={{ 
              fontSize: "16px", 
              color: "white", 
              fontWeight: "bold",
              paddingTop: "20px",
              marginBottom: "10px" 
            }}>
              Description
            </h4>
            <div style={{
              border: "1px solid #ccc",
              padding: "10px",
              height: "155px",
              overflowY: "scroll",
              borderRadius: "5px",
              backgroundColor: "white",
              color: "black",
            }}>
              {politicianData.description
                ? politicianData.description.split("\n").map((paragraph, index) => (
                    <p
                      key={index}
                      style={{
                        fontSize: "12px",
                        lineHeight: "1.4",
                        marginBottom: "10px",
                      }}
                    >
                      {paragraph.trim()}
                    </p>
                  ))
                : <p style={{ fontSize: "12px", color: "#888" }}>No description available.</p>
              }
            </div>
          </div>
        )}
      </div>

      <div>
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: "white",
          fontSize: "12px",
          fontWeight: "bold",
          paddingTop: "10px",
        }}>
          <p>
            Total Raised Amount: ${politicianData.total_raised || 0}
          </p>
          <p>
            Target Amount: ${politicianData.target_amount || 0}
          </p>
        </div>
      </div>

      {/* Progress Bar */}
      <div style={{
        backgroundColor: "#e0e0e0",
        borderRadius: "5px",
        height: "10px",
        position: "relative",
      }}>
        <div style={{
          backgroundColor: "#007bff",
          width: `${Math.min(
            ((politicianData.total_raised || 0) / (politicianData.target_amount || 1)) * 100,
            100
          )}%`,
          height: "100%",
          borderRadius: "5px",
        }}></div>
      </div>

      {/* Donations Table */}
      <div style={{ marginTop: "20px" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            backgroundColor: "white",
            borderRadius: "5px",
            overflow: "hidden",
          }}
        >
          <thead>
            <tr style={{ backgroundColor: "#f2f2f2", height: "40px" }}>
              <th style={{ textAlign: "center", padding: "8px" }}>
                Backer Name
              </th>
              <th style={{ textAlign: "center", padding: "8px" }}>
                Donation Amount in AUD
              </th>
              <th style={{ textAlign: "center", padding: "8px" }}>
                Date & Time
              </th>
            </tr>
          </thead>
          <tbody>
            {politicianData?.donations?.map((donation, index) => (
              <tr
                key={index}
                style={{
                  borderBottom: "1px solid #ccc",
                }}
              >
                <td style={{ textAlign: "center", padding: "8px" }}>
                  {donation.fullName}
                </td>
                <td style={{ textAlign: "center", padding: "8px" }}>
                  ${parseFloat(donation.amount).toFixed(2)}
                </td>
                <td style={{ textAlign: "center", padding: "8px" }}>
                  {new Date(donation.timestamp).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </td>
              </tr>
            ))}
            {(!politicianData?.donations || politicianData.donations.length === 0) && (
              <tr>
                <td colSpan="3" style={{ textAlign: "center", padding: "20px" }}>
                  No donations yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Share Popup */}
      {isSharePopupVisible && (
        <div style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          background: "white",
          color: "black",
          padding: "20px",
          borderRadius: "10px",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.3)",
          zIndex: 1000,
          minWidth: "250px"
        }}>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px"
          }}>
            <h3 style={{ margin: 0 }}>Share this politician</h3>
            <FaTimes
              onClick={() => setIsSharePopupVisible(false)}
              style={{
                cursor: "pointer",
                fontSize: "20px"
              }}
            />
          </div>

          <div style={{
            display: "flex",
            justifyContent: "space-around",
            gap: "20px",
            marginTop: "10px"
          }}>
            {/* Facebook */}
            <div 
              onClick={() => shareToSocialMedia("facebook")}
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                backgroundColor: "#1877f2"
              }}
            >
              <FontAwesomeIcon
                icon={faFacebook}
                style={{
                  color: "white",
                  fontSize: "20px"
                }}
              />
            </div>

            {/* Twitter/X */}
            <div 
              onClick={() => shareToSocialMedia("twitter")}
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                backgroundColor: "black"
              }}
            >
              <FontAwesomeIcon
                icon={faXTwitter}
                style={{
                  color: "white",
                  fontSize: "20px"
                }}
              />
            </div>

            {/* WhatsApp */}
            <div 
              onClick={() => shareToSocialMedia("whatsapp")}
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                backgroundColor: "#25D366"
              }}
            >
              <FontAwesomeIcon
                icon={faWhatsapp}
                style={{
                  color: "white",
                  fontSize: "20px"
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Add DonationBox */}
      {showDonationBox && (
        <DonationBox
          onClose={() => setShowDonationBox(false)}
          selectedPurpose="Support Politician"
          isPolitician={true}
          projectData={{
            project_id: selectedPoliticianId,
          }}
        />
      )}

      {/* Biography Popup */}
      {showBiographyPopup && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "white",
            color: "black",
            padding: "20px",
            borderRadius: "10px",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.3)",
            zIndex: 1000,
          }}
        >
          <p style={{ fontSize: "14px", fontWeight: "bold", marginBottom: "15px" }}>
            {politicianData.biography || "Biography"}
          </p>
          <p style={{ fontSize: "14px", marginBottom: "15px" }}>
            {politicianData.parliamentary_service || "Parliamentary service information not available."}
          </p>
          <div style={{
            display: "flex",
            gap: "10px",
            marginTop: "10px",
            justifyContent: "flex-end"
          }}>
            <button
              onClick={() => window.open(politicianData.profile_url, '_blank')}
              style={{
                padding: "8px 12px",
                background: "linear-gradient(to right, #007bff, #00d4ff)",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
              }}
            >
              Read More
            </button>
            <button
              onClick={() => setShowBiographyPopup(false)}
              style={{
                padding: "8px 12px",
                background: "linear-gradient(to right, #007bff, #00d4ff)",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Politician; 